import { Ref } from 'vue'
import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager from '../../managers/network/DataQueryManager'
import { emailify, rutify } from '../apiUtils'
import { IRequest, RequestStatus } from './RequestsServices'
import { RequestedChange } from './ChangesServices'
import { IUser } from './UsersServices'

export interface IProveedor {
  id: number
  razon_social: string
  rut_empresa: string
  nombre_representante: string
  rut_representante: string
  telefono_representante: string
  email_representante: string
  fecha_creacion: string
  fecha_actualizacion: string
  usuario_creacion: number
  activo: boolean
}

export interface OperatedProveedor extends IProveedor {
  requestData: IRequest | undefined
  changesData: RequestedChange[]
  proveedorUser?: IUser
  status?: RequestStatus
}

export const defaultIProveedor: IProveedor = {
  id: 0,
  razon_social: '',
  rut_empresa: '',
  nombre_representante: '',
  rut_representante: '',
  telefono_representante: '',
  email_representante: '',
  fecha_creacion: '',
  fecha_actualizacion: '',
  usuario_creacion: 0,
  activo: false,
}

export default class ProveedoresServices {
  private bucketID: string | null = null

  constructor() {
    // Registrar el bucket para proveedores
    this.bucketID = dataStorageManager.registerBucket<IProveedor>(
      this.getAllProveedores.bind(this), // Asegúrate de enlazar el contexto correcto
      {
        refreshRatio: 5 * 60 * 1000, // Refrescar cada 5 minutos
        //preload: true, // Preload
        bucketID: BucketDefaultIDs.PROVEEDORES, // ID del bucket
        operations: new Map<string, DMS_Operation>([
          [
            'insertProveedor',
            {
              operation: this.insertProveedor.bind(this),
              reloadAfter: true,
            },
          ],
          [
            'updateProveedor',
            {
              operation: this.updateProveedor.bind(this),
              reloadAfter: true,
            },
          ],
        ]), // Las operaciones se añadiran despues
      },
    )
  }

  startup() {}

  async getAllProveedores(): Promise<IProveedor[]> {
    try {
      const response = await dataQueryManager.addQuery<IProveedor | IProveedor[]>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_select_proveedores`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
      })
      if (response.data && Array.isArray(response.data)) {
        return response.data
      } else if (response.data) {
        return [response.data]
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    }
    return []
  }

  async insertProveedor(
    proveedorData: Omit<IProveedor, 'id' | 'fecha_creacion' | 'fecha_actualizacion'>,
  ): Promise<number | false> {
    try {
      const body = {
        op_insert_proveedor: {
          razon_social: proveedorData.razon_social,
          rut_empresa: rutify(proveedorData.rut_empresa),
          nombre_representante: proveedorData.nombre_representante,
          rut_representante: rutify(proveedorData.rut_representante),
          telefono_representante: proveedorData.telefono_representante,
          email_representante: emailify(proveedorData.email_representante),
          usuario_creacion: proveedorData.usuario_creacion,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_insert_proveedor`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })
      // op_insert_proveedor devuelve el id generado (RETURNING): lo propagamos.
      if (response.data && typeof response.data === 'number') {
        return response.data
      }
    } catch (error) {
      console.error('Error inserting armador:', error)
    }
    return false
  }

  async updateProveedor(proveedorData: IProveedor): Promise<boolean> {
    try {
      // Telefono y email estan cambiados en api. Cambiar temporalmente hasta corregir en api
      const body = {
        op_update_proveedor: {
          razon_social: proveedorData.razon_social,
          rut_empresa: proveedorData.rut_empresa,
          nombre_representante: proveedorData.nombre_representante,
          rut_representante: proveedorData.rut_representante,
          telefono_representante: proveedorData.telefono_representante,
          email_representante: proveedorData.email_representante,
          activo: proveedorData.activo,
          autorizacion: 1,
          id: proveedorData.id,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_update_proveedor`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') {
        return true
      }
    } catch (error) {
      console.error('Error updating armador:', error)
    }
    return false
  }
}
